import { useCallback, useEffect, useMemo, useState } from 'react';
import { Building2, FileText, Globe2, Loader2, Mail, RefreshCw, Search, Users } from 'lucide-react';
import { api, ApiRequestError } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { DataErrorBanner } from '@/components/ui/data-error-banner';

type SurfaceWorkspaceMode = 'tenant' | 'platform';
type SurfaceRequestType = 'booking' | 'rfq' | 'inquiry';

interface TenantOption {
  tenantId: string;
  tenantName: string;
  userCount: number;
  requestCount: number;
}

interface ActiveTenant {
  tenantId: string;
  tenantSlug: string;
  tenantName: string;
}

interface SurfaceUserRow {
  id: string;
  name: string;
  email: string;
  companyName: string;
  emailVerifiedAt?: string | null;
  lastSeenAt?: string | null;
  relationshipStatus?: string | null;
  sourceHost?: string | null;
  firstSeenAt?: string | null;
}

interface SurfaceRequestRow {
  id: string;
  requestType: SurfaceRequestType;
  status: string;
  sourceHost: string;
  contactName: string;
  companyName: string;
  contactEmail: string;
  title: string;
  message: string;
  createdAt?: string | null;
  updatedAt?: string | null;
}

interface SurfaceOverviewResponse {
  activeTenant: ActiveTenant;
  tenantOptions: TenantOption[];
  filters: {
    q: string;
    requestType: SurfaceRequestType | null;
  };
  summary: {
    userCount: number;
    requestCount: number;
    requestTypeCounts: Record<string, number>;
  };
  users: SurfaceUserRow[];
  requests: SurfaceRequestRow[];
}

function formatDateTime(value?: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('en-SG', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function requestTypeLabel(type: SurfaceRequestType): string {
  if (type === 'booking') return 'Booking';
  if (type === 'rfq') return 'RFQ';
  return 'Inquiry';
}

function requestTypeBadgeClass(type: SurfaceRequestType): string {
  if (type === 'booking') return 'bg-blue-100 text-blue-700';
  if (type === 'rfq') return 'bg-amber-100 text-amber-700';
  return 'bg-emerald-100 text-emerald-700';
}

export function SurfaceAccessWorkspace({ mode }: { mode: SurfaceWorkspaceMode }) {
  const [tenantId, setTenantId] = useState<string>('all');
  const [q, setQ] = useState('');
  const [requestType, setRequestType] = useState<'all' | SurfaceRequestType>('all');
  const [data, setData] = useState<SurfaceOverviewResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiRequestError | Error | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (mode === 'platform' && tenantId !== 'all') params.set('tenantId', tenantId);
      if (q.trim()) params.set('q', q.trim());
      if (requestType !== 'all') params.set('requestType', requestType);
      const suffix = params.toString() ? `?${params.toString()}` : '';
      const res = await api.get<SurfaceOverviewResponse>(`/admin/surface/overview${suffix}`);
      setData(res);
      if (mode === 'platform' && tenantId === 'all' && res.activeTenant?.tenantId) {
        setTenantId(res.activeTenant.tenantId);
      }
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to load surface access overview'));
    } finally {
      setLoading(false);
    }
  }, [mode, q, requestType, tenantId]);

  useEffect(() => {
    void load();
  }, [load]);

  const bookingCount = data?.summary.requestTypeCounts.booking ?? 0;
  const rfqCount = data?.summary.requestTypeCounts.rfq ?? 0;
  const inquiryCount = data?.summary.requestTypeCounts.inquiry ?? 0;
  const tenantOptions = data?.tenantOptions ?? [];
  const activeTenantName = data?.activeTenant?.tenantName ?? 'Tenant';
  const headerTitle = mode === 'platform' ? 'Surface Access Console' : 'Surface Users & Requests';
  const headerDesc = mode === 'platform'
    ? 'Cross-tenant view of verified storefront users and public requests.'
    : 'Verified storefront contacts and public requests for your tenant.';

  const requestEmptyLabel = useMemo(() => {
    if (requestType === 'booking') return 'No verified booking requests yet.';
    if (requestType === 'rfq') return 'No verified RFQ requests yet.';
    if (requestType === 'inquiry') return 'No verified inquiry requests yet.';
    return 'No verified public requests yet.';
  }, [requestType]);

  return (
    <div className="mx-auto max-w-7xl space-y-6 px-4 py-6">
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">{headerTitle}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{headerDesc}</p>
          {data?.activeTenant && (
            <div className="mt-3 inline-flex items-center gap-2 rounded-full border bg-card px-3 py-1 text-xs text-muted-foreground">
              <Building2 className="h-3.5 w-3.5" />
              <span>{data.activeTenant.tenantName}</span>
              <span className="text-muted-foreground/60">({data.activeTenant.tenantSlug})</span>
            </div>
          )}
        </div>
        <Button variant="outline" onClick={() => void load()} disabled={loading} className="gap-2">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          Refresh
        </Button>
      </div>

      {error ? (
        <DataErrorBanner
          error={{
            endpoint: '/api/v1/admin/surface/overview',
            status: error instanceof ApiRequestError ? error.status : undefined,
            code: error instanceof ApiRequestError ? error.code : undefined,
            message: error.message,
          }}
          entityLabel="Surface Access"
          onRetry={() => void load()}
        />
      ) : null}

      <Card>
        <CardHeader className="space-y-3">
          <CardTitle className="text-base">Filters</CardTitle>
          <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_220px_220px_auto]">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={q}
                onChange={(event) => setQ(event.target.value)}
                placeholder="Search by name, company, email, title"
                className="pl-9"
              />
            </div>

            {mode === 'platform' ? (
              <Select value={tenantId} onValueChange={setTenantId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select tenant" />
                </SelectTrigger>
                <SelectContent>
                  {tenantOptions.map((option) => (
                    <SelectItem key={option.tenantId} value={option.tenantId}>
                      {option.tenantName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : <div />}

            <Select value={requestType} onValueChange={(value) => setRequestType(value as 'all' | SurfaceRequestType)}>
              <SelectTrigger>
                <SelectValue placeholder="All request types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All request types</SelectItem>
                <SelectItem value="booking">Booking</SelectItem>
                <SelectItem value="rfq">RFQ</SelectItem>
                <SelectItem value="inquiry">Inquiry</SelectItem>
              </SelectContent>
            </Select>

            <Button onClick={() => void load()} disabled={loading}>
              Apply
            </Button>
          </div>
        </CardHeader>
      </Card>

      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="flex items-center gap-3 p-5">
            <Users className="h-5 w-5 text-blue-600" />
            <div>
              <p className="text-xs uppercase tracking-[0.14em] text-muted-foreground">Verified users</p>
              <p className="text-2xl font-semibold">{data?.summary.userCount ?? 0}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex items-center gap-3 p-5">
            <FileText className="h-5 w-5 text-emerald-600" />
            <div>
              <p className="text-xs uppercase tracking-[0.14em] text-muted-foreground">Requests</p>
              <p className="text-2xl font-semibold">{data?.summary.requestCount ?? 0}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5">
            <p className="text-xs uppercase tracking-[0.14em] text-muted-foreground">Booking / RFQ</p>
            <p className="mt-2 text-lg font-semibold">{bookingCount} / {rfqCount}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5">
            <p className="text-xs uppercase tracking-[0.14em] text-muted-foreground">Other inquiry</p>
            <p className="mt-2 text-lg font-semibold">{inquiryCount}</p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="users" className="space-y-4">
        <TabsList>
          <TabsTrigger value="users">Surface Users</TabsTrigger>
          <TabsTrigger value="requests">Public Requests</TabsTrigger>
        </TabsList>

        <TabsContent value="users">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Verified contacts for {activeTenantName}</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-10">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              ) : data && data.users.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Company</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Source host</TableHead>
                      <TableHead>Last seen</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.users.map((user) => (
                      <TableRow key={user.id}>
                        <TableCell>
                          <div className="font-medium text-foreground">{user.name || '—'}</div>
                          <div className="text-xs text-muted-foreground">{formatDateTime(user.firstSeenAt)}</div>
                        </TableCell>
                        <TableCell>{user.companyName || '—'}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Mail className="h-3.5 w-3.5 text-muted-foreground" />
                            <span>{user.email}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary" className="capitalize">
                            {user.relationshipStatus || 'lead'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">{user.sourceHost || '—'}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">{formatDateTime(user.lastSeenAt)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
                  No verified surface users yet.
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="requests">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Public requests for {activeTenantName}</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-10">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              ) : data && data.requests.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Type</TableHead>
                      <TableHead>Contact</TableHead>
                      <TableHead>Title</TableHead>
                      <TableHead>Message</TableHead>
                      <TableHead>Source</TableHead>
                      <TableHead>Created</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.requests.map((request) => (
                      <TableRow key={request.id}>
                        <TableCell>
                          <div className="flex flex-col gap-2">
                            <Badge className={requestTypeBadgeClass(request.requestType)}>
                              {requestTypeLabel(request.requestType)}
                            </Badge>
                            <span className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">{request.status}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="font-medium">{request.contactName}</div>
                          <div className="text-xs text-muted-foreground">{request.companyName}</div>
                          <div className="text-xs text-muted-foreground">{request.contactEmail}</div>
                        </TableCell>
                        <TableCell className="font-medium text-foreground">{request.title}</TableCell>
                        <TableCell>
                          <p className="max-w-[380px] truncate text-sm text-muted-foreground">{request.message}</p>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <Globe2 className="h-3.5 w-3.5" />
                            <span>{request.sourceHost}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">{formatDateTime(request.createdAt)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
                  {requestEmptyLabel}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
